/**
 * User Info Manager
 *
 * Handles user identity synchronization across all external services:
 * - Segment (analytics)
 * - Statsig (feature flags)
 * - Braze (engagement)
 * - Sentry (error tracking)
 *
 * This manager does NOT store state - use GraphQL cache for user data.
 */

import { CustomerInfo } from '@varo/graphql-schema-types';
import {
  resetAnalytics,
  updateCampaignTrackingUser,
} from 'services/analytics/analyticsManager';
import {
  getAnonymousId,
  identifyUser as identifySegmentUser,
} from 'services/analytics/segment/segment';
import { updateEngagementUser } from 'services/engagement/engagementManager';
import {
  resetStatsig,
  updateStatsigUser,
} from 'services/statsig/statsigManager';
import { getAppUpdateProperties } from 'shared/utils/appUpdateInfo/appUpdateInfo';
import {
  logBreadcrumb,
  logError,
  setUserId as setSentryUserId,
} from 'shared/utils/sentry/sentry';

/**
 * Synchronize user identity to all external services (Segment, Statsig, Braze, Sentry)
 * This is a pure function with no state management - it only updates external services.
 *
 * @param customerInfo - CustomerInfo from GraphQL
 * @param identityId - User's identity ID from token
 */
export async function syncUserIdentityToServices(
  customerInfo: CustomerInfo,
  identityId: string,
): Promise<void> {
  try {
    const userId = customerInfo.id ?? '';
    const email = customerInfo.email?.address ?? '';
    const phoneNumber = customerInfo.phone ?? '';

    logBreadcrumb({
      level: 'info',
      message: 'Syncing user identity to external services',
      data: { userId },
    });

    const anonymousId = await getAnonymousId();

    // Update all services in parallel for performance
    await Promise.all([
      identifySegmentUser(identityId, {
        username: email,
        email,
        phone: phoneNumber,
        firstName: customerInfo.name?.first ?? '',
        lastName: customerInfo.name?.last ?? '',
        birthday: customerInfo.dateOfBirth ?? '',
        address: {
          city: customerInfo.address?.city ?? '',
          country: customerInfo.address?.country ?? '',
          postalCode: customerInfo.address?.zip ?? '',
          state: customerInfo.address?.state ?? '',
          street: customerInfo.address?.addressLine1 ?? '',
        },
        ...getAppUpdateProperties(),
      }),
      updateStatsigUser(identityId, anonymousId ?? ''),
    ]);

    // These are synchronous, but group them together
    // Sentry and AppsFlyer use identityId (matches main branch)
    setSentryUserId(identityId);
    updateCampaignTrackingUser(identityId);
    updateEngagementUser(identityId);

    logBreadcrumb({
      level: 'info',
      message: 'User identity synced successfully to all services',
      data: { userId },
    });
  } catch (_error) {
    logError('Failed to sync user identity to services');
    throw _error;
  }
}

/**
 * Clear user identity from all external services (Segment, Statsig, Braze, Sentry)
 * This is a pure function with no state management - it only clears external services.
 *
 * Use this instead of clearUserIdentity() for new code.
 */
export async function clearUserIdentityFromServices(): Promise<void> {
  try {
    logBreadcrumb({
      level: 'info',
      message: 'Clearing user identity from external services',
    });

    await Promise.all([resetStatsig(), resetAnalytics()]);

    setSentryUserId('');

    logBreadcrumb({
      level: 'info',
      message: 'User identity cleared successfully from all services',
    });
  } catch (_error) {
    logError('Failed to clear user identity from services');
    // Don't throw - logout should succeed even if cleanup fails
  }
}

/**
 * Synchronize minimal user identity during signup
 * This is used when we only have basic info (userId, email, phone) before login
 * Full sync with all user data happens after login via syncUserIdentityToServices()
 *
 * @param userId - User ID
 * @param email - User email
 * @param phoneNumber - User phone number
 */
export async function syncMinimalUserIdentity(
  userId: string,
  email: string,
  phoneNumber: string,
): Promise<void> {
  try {
    logBreadcrumb({
      level: 'info',
      message: 'Syncing minimal user identity during signup',
      data: { userId },
    });

    const anonymousId = await getAnonymousId();

    // Update core identity services with minimal data
    setSentryUserId(userId);

    await identifySegmentUser(userId, {
      username: email,
      email,
      phone: phoneNumber,
      ...getAppUpdateProperties(),
    });

    await updateStatsigUser(userId, anonymousId ?? '');

    updateCampaignTrackingUser(userId);

    logBreadcrumb({
      level: 'info',
      message: 'Minimal user identity synced successfully',
      data: { userId },
    });
  } catch (_error) {
    logError('Failed to sync minimal user identity');
    throw _error;
  }
}
